import { useEffect, useState } from 'react';
import { useQuery, gql } from '@apollo/client';
import { MY_INFO_FORM_QUERY } from '../components/MyInfo/operations';
import { safelySetGladlyUser, initGladly } from '../components/GladlyChatBlock/GladlyUtils';

/**
 * @name GLADLY_CHAT_BLOCK_QUERY
 * @description GraphQL query for retrieving Gladly Chat App ID and feature flag status.
 */
const GLADLY_CHAT_BLOCK_QUERY = gql`
  query GladlyChatBlockQuery {
    config {
      gladlyChatAppId: string(name: "gladlyChatAppId")
    }
    isGladlyChatEnabled: featureFlag(
      key: "gladly-chat",
      defaultValue: false
    )
  }
`;

/**
 * @name useGladlyChat
 * @description Custom React hook to initialize and synchronize the Gladly Chat widget with user and configuration state.
 * Handles fetching config, user info, subscribing to digitalData, and initializing Gladly.
 *
 * @type {public}
 * * @returns {Object} Hook state and data
 * @returns {string|null} return.gladlyChatAppId - Gladly Chat App ID (or null if not available)
 * @returns {boolean} return.isGladlyChatEnabled - Whether Gladly Chat is enabled
 * @returns {boolean} return.isClient - True if running on client-side
 * @returns {Object|null} return.userInfo - Current logged-in user info (null if not logged in)
 */
export function useGladlyChat() {
  const [isClient, setIsClient] = useState(false);
  const [userEmail, setUserEmail] = useState(null);

  /**
   * @name useEffect:setIsClient
   * @description Marks when hook is running on client-side (avoids SSR issues).
   * @type {private}
   *  @args {void}
   * @returns {void}
   */
  // mark client-side
  useEffect(() => {
    setIsClient(true);
  }, []);

  /**
   * @name configData
   * @description Fetches Gladly configuration (App ID + feature flag) via GraphQL.
   * @type {private}
   * @returns {Object} GraphQL response object
   */
  const { data: configData = {} } = useQuery(GLADLY_CHAT_BLOCK_QUERY, {
    ssr: false,
    context: { batch: true },
    fetchPolicy: 'cache-and-network',
  });

  const gladlyChatAppId = configData?.config?.gladlyChatAppId ?? null;
  const isGladlyChatEnabled = configData?.isGladlyChatEnabled ?? false;

  // fetch user info
  const { data: userInfoQuery, refetch } = useQuery(MY_INFO_FORM_QUERY, {
    skip: !isClient,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const email = data?.userInfo?.email ?? null;
      setUserEmail(email);
    },
  });

  /**
   * @name gladlyUserIdHandler
   * @description Handles changes in digitalData user.id by refetching user info and syncing with Gladly.
   *
   * @type {private}
   * @args {string|null} userId - The user ID from digitalData subscription.
   * @returns {Promise<void>}
   */
  useEffect(() => {
    if (!isClient) return;
    if (typeof window !== 'undefined' && window.digitalData?.subscribe) {
      const handler = async function gladlyUserIdHandler(userId) {
        if (!userId) {
          setUserEmail(null);
          return;
        }

        try {
          const result = await refetch?.();
          const email = result?.data?.userInfo?.email ?? null;
          setUserEmail(email);
          if (email) {
            safelySetGladlyUser(result.data.userInfo);
          }
        } catch (e) {
          console.error('GladlyTest: refetch error:', e);
        }
      };
      window.digitalData.subscribe(['user.id'], handler);
    }
  }, [isClient, refetch]);

  /**
   * @name syncGladlyUser
   * @description Keeps Gladly in sync with the logged-in user state (sets or clears user).
   *
   * @type {private}
   * @returns {void}
   */
  useEffect(() => {
    if (!isClient) return;

    if (userEmail) {
      safelySetGladlyUser(userInfoQuery?.userInfo);
    } else {
      window.Gladly?.clearUser?.();
    }
  }, [isClient, userEmail, userInfoQuery]);

  /**
   * @name initGladlyChatWidget
   * @description Injects CSS rules for homepage behavior and initializes the Gladly widget if enabled.
   *
   * @type {private}
   * @returns {Function|undefined} cleanup function to remove injected style
   */
  useEffect(() => {
    if (!isClient || !gladlyChatAppId || !isGladlyChatEnabled) {
      return undefined;
    }

    const style = document.createElement('style');
    style.innerHTML = `
      body#home #gladlyChat_container { display: none !important; }
      body#home #gladlyChat_container.wasOpenedOnce { display: flex !important; }
    `;
    document.head.appendChild(style);

    initGladly(gladlyChatAppId);

    return () => {
      style.remove();
    };
  }, [isClient, gladlyChatAppId, isGladlyChatEnabled]);

  return {
    gladlyChatAppId,
    isGladlyChatEnabled,
    isClient,
    userInfo: userInfoQuery?.userInfo ?? null,
  };
}
